import React from 'react';

// One label per approval type, named by what the approver does rather than by
// the stored trigger name. Each has a variant with the approver's step.
const RULE_LABELS = {
    pr: {
        one: (p) => $t('Purchase request approval: {{scope}}', p),
        step: (p) => $t('Purchase request approval: {{scope}}, step {{step}} of {{steps}}', p),
    },
    po: {
        one: (p) => $t('Expenditure approval: {{scope}}', p),
        step: (p) => $t('Expenditure approval: {{scope}}, step {{step}} of {{steps}}', p),
    },
    pf: {
        one: (p) => $t('Position fund approval: {{scope}}', p),
        step: (p) => $t('Position fund approval: {{scope}}, step {{step}} of {{steps}}', p),
    },
    cc: {
        one: (p) => $t('Expense fund approval: {{scope}}', p),
        step: (p) => $t('Expense fund approval: {{scope}}, step {{step}} of {{steps}}', p),
    },
    budget: {
        one: (p) => $t('Budget change approver: {{scope}}', p),
        step: (p) => $t('Budget change approver: {{scope}}, step {{step}} of {{steps}}', p),
    },
};

/**
 * "Purchase request approval: Tournament Operations, step 1 of 2". The scope
 * is the rule's department or other budget line; a rule without one covers
 * every department. A trigger without a label of its own falls back to its
 * stored name, which the server returns already translated.
 */
export function approverRuleLabel(rule) {
    const params = { scope: rule.model_name || $t('all departments'), step: rule.step, steps: rule.steps };
    const labels = RULE_LABELS[rule.kind === 'budget_approval' ? 'budget' : rule.trigger_code];
    const stepped = rule.step && rule.steps > 1;

    let label;
    if (labels) {
        label = stepped ? labels.step(params) : labels.one(params);
    } else {
        const trigger = rule.trigger || $t('Approval');
        label = stepped
            ? $t('{{trigger}}: {{scope}}, step {{step}} of {{steps}}', { trigger, ...params })
            : $t('{{trigger}}: {{scope}}', { trigger, ...params });
    }

    return rule.active ? label : $t('{{rule}} (inactive)', { rule: label });
}

/**
 * Why a user cannot be removed from a project yet: every approval rule on it
 * that names them, and where to change those rules.
 */
export default function ApproverRulesBlockedList({ rules, linkUrl }) {
    return (
        <div className="tw-space-y-2 tw-rounded-lg tw-border-s-4 tw-border-red tw-bg-red-10 tw-px-4 tw-py-3 tw-text-0.5sm tw-text-charcoal" role="alert">
            <p className="tw-mb-0">
                <span className="tw-font-semibold">{$t('Cannot remove yet.')}</span>{' '}
                {$t(
                    'This user is an approver on this project and must be removed from every approval rule first. Any approval type blocks removal (purchase requests, expenditure approvals, direct commitments, budget changes, position fund and expense fund approvals):',
                )}
            </p>
            <ul className="tw-mb-0 tw-list-disc tw-ps-5">
                {rules.map((rule) => (
                    <li key={`${rule.kind}-${rule.approval_settings_id}`}>{approverRuleLabel(rule)}</li>
                ))}
            </ul>
            {linkUrl && (
                <a href={linkUrl} className="tw-inline-block tw-font-medium tw-text-blue hover:tw-underline">
                    {rules.length > 0 && rules.every((rule) => rule.kind === 'budget_approval')
                        ? $t('Open Budget change rules')
                        : $t('Open Approval Management')}
                </a>
            )}
        </div>
    );
}
